import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Music, Calendar, Settings, Image as ImageIcon, Link as LinkIcon, Edit, Plus, MoreHorizontal, Globe, ShieldCheck, FileText, Download, Eye, LayoutTemplate, History as HistoryIcon, User, Mail, Phone, DollarSign, Clock } from 'lucide-react';
const TABS = ['Overview', 'Rooms', 'Programming Rules', 'Contacts', 'Assets', 'Public Page', 'History'];
export function VenueProfilePage() {
  const [activeTab, setActiveTab] = useState('Overview');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-6" data-id="element-5034">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-5035">
        <div data-id="element-5036">
          <h1 className="text-2xl font-bold text-zinc-100" data-id="element-5037">Venue Profile</h1>
          <p className="text-zinc-400" data-id="element-5038">
            Manage your venue's identity, rooms, and programming rules.
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-5039">
          <button className="px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-100 text-sm font-medium rounded-lg transition-colors flex items-center" data-id="element-5040">
            <Globe className="w-4 h-4 mr-2" data-id="element-5041" />
            View Public Page
          </button>
          <button className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-medium rounded-lg transition-colors flex items-center" data-id="element-5042">
            <Edit className="w-4 h-4 mr-2" data-id="element-5043" />
            Edit Profile
          </button>
        </div>
      </div>

      <div className="flex space-x-1 border-b border-zinc-800 overflow-x-auto no-scrollbar" data-id="element-5044">
        {TABS.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab ? 'border-amber-500 text-amber-500' : 'border-transparent text-zinc-400 hover:text-zinc-100 hover:border-zinc-700'}`} data-id="element-5045">
            {tab}
          </button>)}
      </div>

      {activeTab === 'Overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5046">
          <div className="lg:col-span-2 space-y-6" data-id="element-5047">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5048">
              <div className="flex items-start justify-between mb-6" data-id="element-5049">
                <div className="flex items-center space-x-4" data-id="element-5050">
                  <div className="w-16 h-16 bg-zinc-800 rounded-xl flex items-center justify-center" data-id="element-5051">
                    <Music className="w-8 h-8 text-zinc-500" data-id="element-5052" />
                  </div>
                  <div data-id="element-5053">
                    <h2 className="text-xl font-bold text-zinc-100 flex items-center" data-id="element-5054">
                      The Empty Bottle
                      <ShieldCheck className="w-5 h-5 text-green-500 ml-2" data-id="element-5055" />
                    </h2>
                    <p className="text-zinc-400 flex items-center mt-1" data-id="element-5056">
                      <MapPin className="w-4 h-4 mr-1" data-id="element-5057" /> Chicago, IL
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed mb-6" data-id="element-5058">
                Legendary indie rock club in Chicago's Ukrainian Village
                neighborhood. Known for cheap drinks, great sound, and hosting
                bands right before they break big.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-5059">
                <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50" data-id="element-5060">
                  <p className="text-xs text-zinc-500 mb-1" data-id="element-5061">Est.</p>
                  <p className="font-medium text-zinc-100" data-id="element-5062">1992</p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50" data-id="element-5063">
                  <p className="text-xs text-zinc-500 mb-1" data-id="element-5064">Capacity</p>
                  <p className="font-medium text-zinc-100" data-id="element-5065">400</p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50" data-id="element-5066">
                  <p className="text-xs text-zinc-500 mb-1" data-id="element-5067">Age Policy</p>
                  <p className="font-medium text-zinc-100" data-id="element-5068">21+</p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50" data-id="element-5069">
                  <p className="text-xs text-zinc-500 mb-1" data-id="element-5070">Website</p>
                  <p className="font-medium text-amber-500 truncate" data-id="element-5071">
                    emptybottle.com
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-5072">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5073">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-5074">
                Key Stats
              </h3>
              <div className="space-y-4" data-id="element-5075">
                <div className="flex justify-between items-center" data-id="element-5076">
                  <span className="text-zinc-400" data-id="element-5077">Shows This Month</span>
                  <span className="text-zinc-100 font-medium" data-id="element-5078">24</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-5079">
                  <span className="text-zinc-400" data-id="element-5080">Upcoming Bookings</span>
                  <span className="text-zinc-100 font-medium" data-id="element-5081">86</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-5082">
                  <span className="text-zinc-400" data-id="element-5083">Active Event Needs</span>
                  <span className="text-amber-500 font-medium" data-id="element-5084">3</span>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {activeTab === 'Rooms' && <div className="space-y-6" data-id="element-5085">
          <div className="flex justify-end" data-id="element-5086">
            <button className="flex items-center space-x-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-100 text-sm font-medium rounded-lg transition-colors" data-id="element-5087">
              <Plus className="w-4 h-4" data-id="element-5088" />
              <span data-id="element-5089">Add Room</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5090">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5091">
              <div className="flex justify-between items-start mb-4" data-id="element-5092">
                <div data-id="element-5093">
                  <h3 className="text-lg font-bold text-zinc-100" data-id="element-5094">Main Room</h3>
                  <p className="text-zinc-400 text-sm" data-id="element-5095">
                    Primary performance space
                  </p>
                </div>
                <button className="p-2 text-zinc-500 hover:text-zinc-300 transition-colors" data-id="element-5096">
                  <Edit className="w-4 h-4" data-id="element-5097" />
                </button>
              </div>
              <div className="space-y-3 text-sm" data-id="element-5098">
                <div className="flex justify-between" data-id="element-5099">
                  <span className="text-zinc-500" data-id="element-5100">Capacity</span>
                  <span className="text-zinc-100" data-id="element-5101">400 (Standing)</span>
                </div>
                <div className="flex justify-between" data-id="element-5102">
                  <span className="text-zinc-500" data-id="element-5103">Curfew</span>
                  <span className="text-zinc-100" data-id="element-5104">2:00 AM</span>
                </div>
                <div className="flex justify-between" data-id="element-5105">
                  <span className="text-zinc-500" data-id="element-5106">Stage Size</span>
                  <span className="text-zinc-100" data-id="element-5107">20' x 15'</span>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {activeTab === 'Programming Rules' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5108">
          <div className="lg:col-span-2 space-y-6" data-id="element-5109">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5110">
              <div className="flex justify-between items-start mb-6" data-id="element-5111">
                <div data-id="element-5112">
                  <h3 className="text-lg font-medium text-zinc-100" data-id="element-5113">
                    Genre & Style Preferences
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1" data-id="element-5114">
                    What kind of acts do you book?
                  </p>
                </div>
                <button className="text-zinc-500 hover:text-zinc-300" data-id="element-5115">
                  <Edit className="w-4 h-4" data-id="element-5116" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-5117">
                <div data-id="element-5118">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-5119">
                    Primary Genres
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-5120">
                    {['Indie Rock', 'Post-Punk', 'Electronic', 'Experimental'].map(genre => <span key={genre} className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300" data-id="element-5121">
                        {genre}
                      </span>)}
                    <button className="px-3 py-1.5 bg-zinc-900/50 border border-zinc-800 border-dashed rounded-lg text-sm text-zinc-500 hover:text-zinc-300 transition-colors flex items-center" data-id="element-5122">
                      <Plus className="w-3 h-3 mr-1" data-id="element-5123" /> Add
                    </button>
                  </div>
                </div>
                <div data-id="element-5124">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-5125">
                    No-Go Genres
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-5126">
                    {['Country', 'Top 40 Covers', 'Hard Hardcore'].map(genre => <span key={genre} className="px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400" data-id="element-5127">
                          {genre}
                        </span>)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5128">
              <div className="flex justify-between items-start mb-6" data-id="element-5129">
                <div data-id="element-5130">
                  <h3 className="text-lg font-medium text-zinc-100" data-id="element-5131">
                    Budget Bands & Deals
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1" data-id="element-5132">
                    Typical financial structures by day of week.
                  </p>
                </div>
                <button className="text-zinc-500 hover:text-zinc-300" data-id="element-5133">
                  <Edit className="w-4 h-4" data-id="element-5134" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-5135">
                <div className="grid grid-cols-3 gap-4 p-4 bg-zinc-900/50 border border-zinc-800/50 rounded-lg" data-id="element-5136">
                  <div data-id="element-5137">
                    <div className="text-sm font-medium text-zinc-100" data-id="element-5138">
                      Weekends (Fri-Sat)
                    </div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-5139">Main Room</div>
                  </div>
                  <div data-id="element-5140">
                    <div className="text-sm text-zinc-300" data-id="element-5141">$1,000 - $3,000</div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-5142">
                      Typical Guarantee
                    </div>
                  </div>
                  <div data-id="element-5143">
                    <div className="text-sm text-zinc-300" data-id="element-5144">
                      Vs 80% after expenses
                    </div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-5145">
                      Typical Deal
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 p-4 bg-zinc-900/50 border border-zinc-800/50 rounded-lg" data-id="element-5146">
                  <div data-id="element-5147">
                    <div className="text-sm font-medium text-zinc-100" data-id="element-5148">
                      Weekdays (Sun-Thu)
                    </div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-5149">Main Room</div>
                  </div>
                  <div data-id="element-5150">
                    <div className="text-sm text-zinc-300" data-id="element-5151">$300 - $800</div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-5152">
                      Typical Guarantee
                    </div>
                  </div>
                  <div data-id="element-5153">
                    <div className="text-sm text-zinc-300" data-id="element-5154">
                      Door Deal / Split
                    </div>
                    <div className="text-xs text-zinc-500 mt-1" data-id="element-5155">
                      Typical Deal
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-5156">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5157">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-5158">
                Recurring Patterns
              </h3>
              <div className="space-y-3" data-id="element-5159">
                <div className="flex items-start space-x-3 p-3 bg-zinc-900/50 border border-zinc-800/50 rounded-lg" data-id="element-5160">
                  <Calendar className="w-5 h-5 text-zinc-500 shrink-0 mt-0.5" data-id="element-5161" />
                  <div data-id="element-5162">
                    <p className="text-sm font-medium text-zinc-300" data-id="element-5163">
                      Free Mondays
                    </p>
                    <p className="text-xs text-zinc-500 mt-1" data-id="element-5164">
                      Every Monday is a free show featuring local talent. No
                      guarantees, door split only.
                    </p>
                  </div>
                </div>
              </div>
              <button className="w-full mt-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-sm font-medium rounded-lg transition-colors flex items-center justify-center" data-id="element-5165">
                <Plus className="w-4 h-4 mr-2" data-id="element-5166" /> Add Pattern
              </button>
            </div>
          </div>
        </div>}

      {activeTab === 'Contacts' && <div className="space-y-6" data-id="element-5167">
          <div className="flex justify-end" data-id="element-5168">
            <button className="flex items-center space-x-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-medium rounded-lg transition-colors" data-id="element-5169">
              <Plus className="w-4 h-4" data-id="element-5170" />
              <span data-id="element-5171">Add Contact</span>
            </button>
          </div>

          <div className="bg-[#14141A] border border-zinc-800 rounded-xl overflow-hidden" data-id="element-5172">
            <div className="p-4 border-b border-zinc-800 bg-zinc-900/30" data-id="element-5173">
              <h3 className="font-medium text-zinc-100" data-id="element-5174">Booking & Talent</h3>
            </div>
            <table className="w-full text-left text-sm" data-id="element-5175">
              <thead className="bg-zinc-900/50 border-b border-zinc-800 text-zinc-400" data-id="element-5176">
                <tr data-id="element-5177">
                  <th className="px-4 py-3 font-medium" data-id="element-5178">Name</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5179">Role</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5180">Email</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5181">Phone</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-5182">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800" data-id="element-5183">
                <tr className="hover:bg-zinc-900/50 transition-colors" data-id="element-5184">
                  <td className="px-4 py-3 font-medium text-zinc-100 flex items-center" data-id="element-5185">
                    <User className="w-4 h-4 mr-2 text-zinc-500" data-id="element-5186" /> Brent Heyl
                  </td>
                  <td className="px-4 py-3 text-zinc-400" data-id="element-5187">Talent Buyer</td>
                  <td className="px-4 py-3 text-zinc-300 flex items-center" data-id="element-5188">
                    <Mail className="w-3 h-3 mr-2 text-zinc-500" data-id="element-5189" />{' '}
                    booking@emptybottle.com
                  </td>
                  <td className="px-4 py-3 text-zinc-400" data-id="element-5190">--</td>
                  <td className="px-4 py-3 text-right" data-id="element-5191">
                    <button className="text-zinc-500 hover:text-zinc-300" data-id="element-5192">
                      <MoreHorizontal className="w-4 h-4" data-id="element-5193" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-[#14141A] border border-zinc-800 rounded-xl overflow-hidden" data-id="element-5194">
            <div className="p-4 border-b border-zinc-800 bg-zinc-900/30" data-id="element-5195">
              <h3 className="font-medium text-zinc-100" data-id="element-5196">
                Production & Operations
              </h3>
            </div>
            <table className="w-full text-left text-sm" data-id="element-5197">
              <thead className="bg-zinc-900/50 border-b border-zinc-800 text-zinc-400" data-id="element-5198">
                <tr data-id="element-5199">
                  <th className="px-4 py-3 font-medium" data-id="element-5200">Name</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5201">Role</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5202">Email</th>
                  <th className="px-4 py-3 font-medium" data-id="element-5203">Phone</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-5204">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800" data-id="element-5205">
                <tr className="hover:bg-zinc-900/50 transition-colors" data-id="element-5206">
                  <td className="px-4 py-3 font-medium text-zinc-100 flex items-center" data-id="element-5207">
                    <User className="w-4 h-4 mr-2 text-zinc-500" data-id="element-5208" /> Production
                    Team
                  </td>
                  <td className="px-4 py-3 text-zinc-400" data-id="element-5209">Advancing / Tech</td>
                  <td className="px-4 py-3 text-zinc-300 flex items-center" data-id="element-5210">
                    <Mail className="w-3 h-3 mr-2 text-zinc-500" data-id="element-5211" />{' '}
                    production@emptybottle.com
                  </td>
                  <td className="px-4 py-3 text-zinc-400" data-id="element-5212">(773) 276-3600</td>
                  <td className="px-4 py-3 text-right" data-id="element-5213">
                    <button className="text-zinc-500 hover:text-zinc-300" data-id="element-5214">
                      <MoreHorizontal className="w-4 h-4" data-id="element-5215" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>}

      {activeTab === 'Assets' && <div className="space-y-6" data-id="element-5216">
          <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5217">
            <div className="flex justify-between items-center mb-6" data-id="element-5218">
              <h3 className="text-lg font-medium text-zinc-100" data-id="element-5219">
                Venue Photos
              </h3>
              <button className="flex items-center space-x-2 px-3 py-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-sm font-medium rounded-lg transition-colors" data-id="element-5220">
                <Plus className="w-4 h-4" data-id="element-5221" />
                <span data-id="element-5222">Upload</span>
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-5223">
              {[1, 2, 3, 4].map(i => <div key={i} className="aspect-video bg-zinc-900 border border-zinc-800 rounded-lg flex items-center justify-center group relative overflow-hidden" data-id="element-5224">
                  <ImageIcon className="w-8 h-8 text-zinc-700" data-id="element-5225" />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5226">
                    <button className="p-2 bg-zinc-800 rounded-lg text-zinc-100 hover:bg-zinc-700 transition-colors" data-id="element-5227">
                      <Eye className="w-4 h-4" data-id="element-5228" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5229">
            <div className="flex justify-between items-center mb-6" data-id="element-5230">
              <h3 className="text-lg font-medium text-zinc-100" data-id="element-5231">
                Documents & Specs
              </h3>
              <button className="flex items-center space-x-2 px-3 py-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-sm font-medium rounded-lg transition-colors" data-id="element-5232">
                <Plus className="w-4 h-4" data-id="element-5233" />
                <span data-id="element-5234">Upload Document</span>
              </button>
            </div>
            <div className="space-y-3" data-id="element-5235">
              {[{
            name: 'Tech Rider & Stage Plot (Main Room)',
            type: 'PDF',
            size: '2.4 MB',
            date: 'Updated Jan 2026'
          }, {
            name: 'Venue W-9 Form',
            type: 'PDF',
            size: '1.1 MB',
            date: 'Updated Jan 2026'
          }, {
            name: 'Parking & Load-in Guide',
            type: 'PDF',
            size: '3.5 MB',
            date: 'Updated Nov 2025'
          }].map((doc, i) => <div key={i} className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800/50 hover:border-zinc-700 transition-colors group" data-id="element-5236">
                  <div className="flex items-center space-x-4" data-id="element-5237">
                    <div className="p-2 bg-zinc-800 rounded-lg text-zinc-400" data-id="element-5238">
                      <FileText className="w-5 h-5" data-id="element-5239" />
                    </div>
                    <div data-id="element-5240">
                      <div className="font-medium text-zinc-100" data-id="element-5241">
                        {doc.name}
                      </div>
                      <div className="text-xs text-zinc-500" data-id="element-5242">
                        {doc.type} • {doc.size} • {doc.date}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5243">
                    <button className="p-2 text-zinc-400 hover:text-zinc-100 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors" data-id="element-5244">
                      <Download className="w-4 h-4" data-id="element-5245" />
                    </button>
                    <button className="p-2 text-zinc-400 hover:text-zinc-100 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors" data-id="element-5246">
                      <MoreHorizontal className="w-4 h-4" data-id="element-5247" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {activeTab === 'Public Page' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5248">
          <div className="lg:col-span-1 space-y-6" data-id="element-5249">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5250">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-5251">
                Page Settings
              </h3>
              <div className="space-y-4" data-id="element-5252">
                <div data-id="element-5253">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-5254">
                    Custom URL
                  </label>
                  <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-id="element-5255">
                    <span className="px-3 py-2 text-zinc-500 text-sm bg-zinc-900 border-r border-zinc-800" data-id="element-5256">
                      booker.com/
                    </span>
                    <input type="text" defaultValue="emptybottle" className="flex-1 bg-transparent px-3 py-2 text-sm text-zinc-100 focus:outline-none" data-id="element-5257" />
                  </div>
                </div>
                <div data-id="element-5258">
                  <label className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-5259">
                    Inquiry Form
                  </label>
                  <div className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800/50 rounded-lg" data-id="element-5260">
                    <span className="text-sm text-zinc-300" data-id="element-5261">
                      Accept Public Inquiries
                    </span>
                    <div className="w-10 h-6 bg-green-500 rounded-full relative cursor-pointer" data-id="element-5262">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" data-id="element-5263"></div>
                    </div>
                  </div>
                </div>
                <button className="w-full py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-100 text-sm font-medium rounded-lg transition-colors flex items-center justify-center" data-id="element-5264">
                  <LinkIcon className="w-4 h-4 mr-2" data-id="element-5265" /> Copy Public Link
                </button>
              </div>
            </div>

            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6" data-id="element-5266">
              <h3 className="text-lg font-medium text-zinc-100 mb-4" data-id="element-5267">
                Embed Widget
              </h3>
              <p className="text-sm text-zinc-400 mb-4" data-id="element-5268">
                Embed your booking form directly on your own website.
              </p>
              <button className="w-full py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-100 text-sm font-medium rounded-lg transition-colors flex items-center justify-center" data-id="element-5269">
                <LayoutTemplate className="w-4 h-4 mr-2" data-id="element-5270" /> Configure Embed
              </button>
            </div>
          </div>

          <div className="lg:col-span-2" data-id="element-5271">
            <div className="bg-[#14141A] border border-zinc-800 rounded-xl p-6 h-full min-h-[500px] flex flex-col" data-id="element-5272">
              <div className="flex justify-between items-center mb-6" data-id="element-5273">
                <h3 className="text-lg font-medium text-zinc-100" data-id="element-5274">
                  Live Preview
                </h3>
                <div className="flex items-center space-x-2" data-id="element-5275">
                  <span className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" data-id="element-5276"></span>
                  <span className="w-3 h-3 rounded-full bg-amber-500/20 border border-amber-500/50" data-id="element-5277"></span>
                  <span className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" data-id="element-5278"></span>
                </div>
              </div>
              <div className="flex-1 border border-zinc-800 rounded-lg bg-zinc-950 flex items-center justify-center relative overflow-hidden" data-id="element-5279">
                <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80&w=1000')] bg-cover bg-center" data-id="element-5280"></div>
                <div className="relative z-10 text-center max-w-md p-8 bg-black/60 backdrop-blur-md border border-zinc-800 rounded-xl" data-id="element-5281">
                  <h2 className="text-2xl font-bold text-white mb-2" data-id="element-5282">
                    The Empty Bottle
                  </h2>
                  <p className="text-zinc-300 mb-6" data-id="element-5283">Chicago, IL • 400 Cap</p>
                  <button className="w-full py-3 bg-amber-500 text-black font-bold rounded-lg" data-id="element-5284">
                    Submit Booking Inquiry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {activeTab === 'History' && <div className="bg-[#14141A] border border-zinc-800 rounded-xl overflow-hidden" data-id="element-5285">
          <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/30" data-id="element-5286">
            <h3 className="font-medium text-zinc-100" data-id="element-5287">Booking History</h3>
            <div className="flex items-center space-x-2" data-id="element-5288">
              <button className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 text-sm font-medium rounded-lg transition-colors" data-id="element-5289">
                Export CSV
              </button>
            </div>
          </div>
          <table className="w-full text-left text-sm" data-id="element-5290">
            <thead className="bg-zinc-900/50 border-b border-zinc-800 text-zinc-400" data-id="element-5291">
              <tr data-id="element-5292">
                <th className="px-4 py-3 font-medium" data-id="element-5293">Date</th>
                <th className="px-4 py-3 font-medium" data-id="element-5294">Headliner</th>
                <th className="px-4 py-3 font-medium" data-id="element-5295">Room</th>
                <th className="px-4 py-3 font-medium" data-id="element-5296">Tickets Sold</th>
                <th className="px-4 py-3 font-medium" data-id="element-5297">Gross Gross</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-5298">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800" data-id="element-5299">
              {[{
            date: 'Jun 12, 2026',
            artist: 'Neon Dreams',
            room: 'Main Room',
            tickets: '385 / 400',
            gross: '$7,700'
          }, {
            date: 'Jun 10, 2026',
            artist: 'Local Showcase',
            room: 'Main Room',
            tickets: '210 / 400',
            gross: '$3,150'
          }, {
            date: 'Jun 05, 2026',
            artist: 'The Midnight Echo',
            room: 'Main Room',
            tickets: '400 / 400',
            gross: '$10,000'
          }, {
            date: 'Jun 01, 2026',
            artist: 'Free Monday',
            room: 'Main Room',
            tickets: 'N/A',
            gross: 'N/A'
          }].map((row, i) => <tr key={i} className="hover:bg-zinc-900/50 transition-colors" data-id="element-5300">
                  <td className="px-4 py-3 text-zinc-400" data-id="element-5301">{row.date}</td>
                  <td className="px-4 py-3 font-medium text-zinc-100" data-id="element-5302">
                    {row.artist}
                  </td>
                  <td className="px-4 py-3 text-zinc-300" data-id="element-5303">{row.room}</td>
                  <td className="px-4 py-3 text-zinc-300" data-id="element-5304">{row.tickets}</td>
                  <td className="px-4 py-3 text-green-500" data-id="element-5305">{row.gross}</td>
                  <td className="px-4 py-3 text-right" data-id="element-5306">
                    <button className="text-amber-500 hover:text-amber-400 text-xs font-medium" data-id="element-5307">
                      View Settlement
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </motion.div>;
}